import React, { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type PageMeta = {
  title: string;
  subtitle?: string;
};

type PageMetaContextValue = {
  meta: PageMeta;
  setMeta: (next: Partial<PageMeta>) => void;
};

const PageMetaContext = createContext<PageMetaContextValue | null>(null);

type PageMetaProviderProps = {
  initial: PageMeta;
  children: ReactNode;
};

export const PageMetaProvider: FC<PageMetaProviderProps> = ({ initial, children }) => {
  const [meta, setMetaState] = useState<PageMeta>(initial);

  useEffect(() => {
    setMetaState({ title: initial.title, subtitle: initial.subtitle });
  }, [initial.title, initial.subtitle]);

  const setMeta = useCallback((next: Partial<PageMeta>) => {
    setMetaState((current) => {
      const nextTitle = next.title ?? current.title;
      const nextSubtitle = next.subtitle ?? current.subtitle;

      if (current.title === nextTitle && current.subtitle === nextSubtitle) {
        return current;
      }

      return { title: nextTitle, subtitle: nextSubtitle };
    });
  }, []);

  const value = useMemo(() => ({ meta, setMeta }), [meta, setMeta]);

  return <PageMetaContext.Provider value={value}>{children}</PageMetaContext.Provider>;
};

export const usePageMeta = (): PageMetaContextValue => {
  const context = useContext(PageMetaContext);
  if (!context) {
    throw new Error("usePageMeta must be used within PageMetaProvider");
  }
  return context;
};

export const useSetPageMeta = (title: string, subtitle?: string): void => {
  const { setMeta } = usePageMeta();

  useEffect(() => {
    setMeta({ title, subtitle });
  }, [title, subtitle, setMeta]);
};
